"use client";

import { useTranslations } from "next-intl";
import type { ReactNode, RefObject } from "react";
import { CloudUploadIcon, Loader2Icon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export function AttachPanelBody({
  toolbar,
  viewport,
  footer,
  overlay,
}: {
  toolbar: ReactNode;
  viewport: ReactNode;
  footer: ReactNode;

  overlay?: ReactNode;
}) {
  return (
    <div className="relative flex min-h-0 flex-col">
      <div
        className={cn("flex min-h-0 flex-1 flex-col", overlay && "invisible")}
        aria-hidden={overlay ? true : undefined}
        inert={overlay ? true : undefined}
      >
        <div className="flex min-h-0 flex-1 flex-col border-b">
          {toolbar}
          {viewport}
        </div>
        {footer}
      </div>
      {overlay ? <div className="absolute inset-0 flex min-h-0 flex-col">{overlay}</div> : null}
    </div>
  );
}

export function AttachUploadToolbarRow({
  uploadLabel,
  hint,
  hidden,
  leading,
  fileInputRef,
  accept,
  uploading,
  uploadDisabled,
  trailing,
  startActions,
  onFileChange,
  onUploadClick,
}: {
  uploadLabel: string;
  hint: string;
  hidden?: boolean;
  leading?: ReactNode;
  fileInputRef?: RefObject<HTMLInputElement | null>;
  accept?: string;
  uploading?: boolean;
  /** 当前分组只读（共享分组没有写权限）时禁用上传 */
  uploadDisabled?: boolean;

  trailing?: ReactNode;

  startActions?: ReactNode;
  onFileChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onUploadClick?: () => void;
}) {
  return (
    <div className="flex shrink-0 items-start gap-2 px-4 py-2.5 md:items-center md:gap-3">
      {leading}
      <div
        className={cn(
          "flex min-w-0 flex-1 flex-wrap items-center gap-3",
          hidden && "invisible pointer-events-none",
        )}
        aria-hidden={hidden || undefined}
      >
        {fileInputRef ? (
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={accept}
            className="hidden"
            onChange={onFileChange}
          />
        ) : null}
        {startActions ? (
          <div className="flex shrink-0 items-center">{startActions}</div>
        ) : null}
        <div className="ml-auto flex min-w-0 shrink-0 items-center gap-3">
          {trailing}
          <span className="truncate text-xs text-muted-foreground max-sm:hidden">
            {hint}
          </span>
          <Button
            type="button"
            size="sm"
            disabled={uploading || uploadDisabled}
            tabIndex={hidden ? -1 : undefined}
            onClick={onUploadClick}
          >
            {uploading ? (
              <Loader2Icon className="animate-spin" />
            ) : (
              <CloudUploadIcon />
            )}
            {uploadLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function AttachSplitPanel({
  sidebar,
  open,
  onClose,
  children,
}: {
  sidebar: ReactNode;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}) {
  const t = useTranslations("site.attach");

  return (
    <div className="relative grid min-h-0 flex-1 grid-cols-1 overflow-hidden md:grid-cols-[220px_minmax(0,1fr)] md:overflow-visible">
      <div
        className={cn(
          "absolute inset-y-0 left-0 z-20 w-60 max-w-[80%] bg-background shadow-xl transition-transform duration-200",
          open ? "translate-x-0" : "pointer-events-none -translate-x-full",
          "md:pointer-events-auto md:relative md:z-auto md:w-auto md:max-w-none md:translate-x-0 md:bg-transparent md:shadow-none",
        )}
      >
        {sidebar}
      </div>
      {open ? (
        <button
          type="button"
          aria-label={t("closeGroups")}
          onClick={onClose}
          className="absolute inset-0 z-10 bg-black/40 md:hidden"
        />
      ) : null}
      {children}
    </div>
  );
}
